import { useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import Lenis from 'lenis'
import { Header } from '@components/layout/Header/Header'
import { GlobalBackground } from '@components/layout/GlobalBackground'
import { ScrollToTop } from '@components/common/ScrollToTop/ScrollToTop'

// Pages
import Home from '@pages/Home/Home'
import About from '@pages/About/About'
import MDMessage from '@pages/MDMessage/MDMessage'
import VisionMission from '@pages/VisionMission/VisionMission'
import Certifications from '@pages/Certifications/Certifications'
import BusinessVerticals from '@pages/BusinessVerticals/BusinessVerticals'
import Services from '@pages/Services/Services'
import Projects from '@pages/Projects/Projects'
import Timeline from '@pages/Timeline/Timeline'
import FutureVision from '@pages/FutureVision/FutureVision'
import Advantage from '@pages/Advantage/Advantage'
import Partners from '@pages/Partners/Partners'
import Clients from '@pages/Clients/Clients'
import Infrastructure from '@pages/Infrastructure/Infrastructure'
import Methodology from '@pages/Methodology/Methodology'
import CSR from '@pages/CSR/CSR'
import Team from '@pages/Team/Team'
import Awards from '@pages/Awards/Awards'
import Products from '@pages/Products/Products'
import ExpansionMap from '@pages/ExpansionMap/ExpansionMap'
import Contact from '@pages/Contact/Contact'

function App() {
  // Initialize Lenis Smooth Scroll — DESKTOP ONLY.
  // On touch devices Lenis hijacks native scrolling and causes constant
  // repaints -> flickering while scrolling (especially with backdrop-filter
  // glass cards). Native touch scroll is already smooth, so skip it.
  useEffect(() => {
    const isTouch =
      typeof window !== 'undefined' &&
      (window.matchMedia('(hover: none) and (pointer: coarse)').matches ||
        'ontouchstart' in window)
    if (isTouch) return

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
    })

    function raf(time: number) {
      lenis.raf(time)
      requestAnimationFrame(raf)
    }

    requestAnimationFrame(raf)

    return () => {
      lenis.destroy()
    }
  }, [])

  return (
    <GlobalBackground>
      <div className="flex min-h-screen flex-col">
        <ScrollToTop />
        <Header />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/managing-director-message" element={<MDMessage />} />
            <Route path="/vision-mission-values" element={<VisionMission />} />
            <Route path="/certifications" element={<Certifications />} />
            <Route path="/business-verticals" element={<BusinessVerticals />} />
            <Route path="/services" element={<Services />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/timeline" element={<Timeline />} />
            <Route path="/future-vision-2030" element={<FutureVision />} />
            <Route path="/competitive-advantage" element={<Advantage />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/infrastructure" element={<Infrastructure />} />
            <Route path="/methodology" element={<Methodology />} />
            <Route path="/csr" element={<CSR />} />
            <Route path="/team" element={<Team />} />
            <Route path="/awards" element={<Awards />} />
            <Route path="/products" element={<Products />} />
            <Route path="/expansion" element={<ExpansionMap />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </main>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#FFFFFF',
              color: '#0F172A',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10B981',
                secondary: '#FFFFFF',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#EF4444',
                secondary: '#FFFFFF',
              },
            },
          }}
        />
      </div>
    </GlobalBackground>
  )
}

export default App